/**
 * What a Report a problem / Suggest report records to help troubleshooting
 * (#659, FEEDBACK-PLAN.md §11.2): the build, the screen, the device, the
 * connection and the recent errors. Sent by the device in one Diagnostics
 * object; the server checks its shape and size (@higgins/api-feedback) and
 * keeps it as JSON for the triage item page.
 *
 * The site, its address and the CRM the app uses are NOT here: the server sets
 * those from the request and the app's settings, never from the device.
 *
 * Pure functions over facts read elsewhere (@higgins/ui reads the browser), so
 * this file is tested without one. Type-only imports: node's test runner loads
 * this file on its own.
 */
import type { RecentErrors } from './api-client';

/** From the deploy workflow: VITE_APP_VERSION, VITE_APP_COMMIT, VITE_APP_BRANCH, VITE_APP_BUILT_AT. */
export interface BuildInfo {
  /** The short label the footer shows ("1a2b3c4 · 28 Oct 2026"). */
  version?: string;
  /** The full commit. */
  commit?: string;
  branch?: string;
  /** ISO time the build ran. */
  builtAt?: string;
}

/** What the browser says about itself when the user presses Send. */
export interface DeviceFacts {
  userAgent: string;
  maxTouchPoints: number;
  screenWidth: number;
  screenHeight: number;
  /** From the viewport, not the screen: iOS reports its screen the same way up whichever way the iPad is held. */
  landscape: boolean;
  /** Opened from a home-screen icon (installed web app), not a browser tab. */
  homeScreenApp: boolean;
  online: boolean;
  /** The address path the user was on. */
  path: string;
}

export interface FeedbackDiagnostics {
  build: BuildInfo;
  screen: {
    /** A plain name the app gives the screen ("Take off"), when it does. */
    name?: string;
    path: string;
  };
  device: {
    userAgent: string;
    /** "iPadOS 17.4", "Windows 10 or 11", "Android 14"; '' when the user agent doesn't say. */
    os: string;
    /** The screen in CSS pixels, as the browser reports it ("1024 × 1366"). */
    screenSize: string;
    orientation: 'portrait' | 'landscape';
    homeScreenApp: boolean;
  };
  connection: {
    /** Whether the device thought it had a connection when the user pressed Send. */
    onlineAtCapture: boolean;
    /** Set by the feedback queue when the report had to wait on the device for a signal. */
    waitedOffline?: boolean;
    /** Changes still waiting to sync to CRM (Measure2Quote's outbox), when the app has them. */
    changesToSync?: number;
  };
  recentErrors: RecentErrors;
}

const version = (major: string, minor?: string) => (minor && minor !== '0' ? `${major}.${minor}` : major);

/**
 * The operating system and its version, from the user agent. Some browsers freeze part of it
 * (Windows 10 and 11 both say "NT 10.0"; an iPad asking for desktop sites says it is a Mac), so
 * this says only what can be known.
 */
export function describeOs(userAgent: string, maxTouchPoints = 0): string {
  const apple = /(iPhone|iPad|iPod).*? OS (\d+)[_.](\d+)/.exec(userAgent);
  if (apple) return `${apple[1] === 'iPad' ? 'iPadOS' : 'iOS'} ${version(apple[2], apple[3])}`;
  if (/Macintosh/.test(userAgent) && maxTouchPoints > 1) {
    // An iPad asking for desktop sites: Safari's own version follows iPadOS's.
    const safari = /Version\/(\d+)\.(\d+)/.exec(userAgent);
    return safari ? `iPadOS ${version(safari[1], safari[2])} (from Safari's version)` : 'iPadOS';
  }
  const android = /Android (\d+)(?:\.(\d+))?/.exec(userAgent);
  if (android) return `Android ${version(android[1], android[2])}`;
  if (/Windows NT 10\.0/.test(userAgent)) return 'Windows 10 or 11';
  const windows = /Windows NT (\d+\.\d+)/.exec(userAgent);
  if (windows) return `Windows NT ${windows[1]}`;
  if (/CrOS/.test(userAgent)) return 'ChromeOS';
  if (/Macintosh/.test(userAgent)) return 'macOS';
  if (/Linux/.test(userAgent)) return 'Linux';
  return '';
}

/** Only what was set: an app whose workflow doesn't stamp a value leaves it out. */
function presentOnly(build: BuildInfo): BuildInfo {
  return Object.fromEntries(Object.entries(build).filter(([, v]) => typeof v === 'string' && v.trim())) as BuildInfo;
}

/** The report's Diagnostics, from the browser's facts, the build and what the app adds. */
export function feedbackDiagnostics(input: {
  facts: DeviceFacts;
  build: BuildInfo;
  recentErrors: RecentErrors;
  screenName?: string;
  changesToSync?: number;
}): FeedbackDiagnostics {
  const { facts } = input;
  return {
    build: presentOnly(input.build),
    screen: { ...(input.screenName ? { name: input.screenName } : {}), path: facts.path },
    device: {
      userAgent: facts.userAgent,
      os: describeOs(facts.userAgent, facts.maxTouchPoints),
      screenSize: `${facts.screenWidth} × ${facts.screenHeight}`,
      orientation: facts.landscape ? 'landscape' : 'portrait',
      homeScreenApp: facts.homeScreenApp,
    },
    connection: {
      onlineAtCapture: facts.online,
      ...(input.changesToSync !== undefined ? { changesToSync: input.changesToSync } : {}),
    },
    recentErrors: input.recentErrors,
  };
}
